import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Bookmark, Check, FolderOpen, Info, Library, Plus, RotateCw, Trash2, X } from 'lucide-react';
import Button from '../../ui/Button';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { microInteractions, pageTransitions, springs } from '../../../utils/animations';
import { formatWeekdayMonthDay, parseIsoDate } from '../../../utils/playlist';
import { TONE_CLASSES } from '../../../utils/tones';
import {
  COPY,
  SAVED_COPY,
  deleteConfirm,
  deleteLabel,
  openLabel,
  savedCount,
  savedRowMeta,
} from './playlistCopy';

const TITLE_ID = 'saved-playlists-title';

const serviceDay = (iso) => {
  const date = parseIsoDate(iso);
  return date ? formatWeekdayMonthDay(date) : null;
};

function StatusPill({ delivered }) {
  return (
    <span
      data-testid="saved-playlist-status"
      className={`inline-flex items-center gap-1 flex-shrink-0 px-2 py-0.5 rounded-full text-caption font-medium ${delivered
        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400'
        : 'bg-gray-100 dark:bg-elevated-dark-3 text-gray-700 dark:text-gray-300'}`}
    >
      {delivered
        ? <Check className="w-3 h-3" strokeWidth={3} aria-hidden="true" />
        : <span className="w-1.5 h-1.5 rounded-full border border-current" aria-hidden="true" />}
      {delivered ? SAVED_COPY.delivered : SAVED_COPY.draft}
    </span>
  );
}

// The first item's thumbnail, with the edges of the next two behind it.
function Cover({ items }) {
  const [first, ...rest] = items;
  const layers = rest.slice(0, 2);
  return (
    <div className="relative flex-shrink-0 w-16 h-9 mt-0.5" aria-hidden="true">
      {layers.map((item, i) => (
        <div
          key={`${item.design_id}-${i}`}
          className="absolute inset-0 rounded-md bg-gray-200 dark:bg-elevated-dark-3 border border-white/60 dark:border-white/10"
          style={{ transform: `translate(${(layers.length - i) * 3}px, ${-(layers.length - i) * 3}px)` }}
        />
      ))}
      <div className="absolute inset-0 rounded-md overflow-hidden bg-gray-200 dark:bg-elevated-dark-3 border border-white/60 dark:border-white/10 shadow-soft">
        {first?.thumbnail_url && <img src={first.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />}
      </div>
    </div>
  );
}

function InlineConfirm({ tone, message, children, testId }) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
      className="overflow-hidden"
    >
      <div role="group" aria-label={message} data-testid={testId} className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
        <div className="flex items-start gap-2.5 mb-3">
          <div className={`flex-shrink-0 p-1.5 rounded-lg ${tone === 'danger'
            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
            : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'}`}
          >
            <AlertTriangle className="w-4 h-4" />
          </div>
          <p className="text-body-sm font-semibold text-gray-900 dark:text-gray-100 min-w-0">{message}</p>
        </div>
        <div className="flex flex-wrap gap-2">{children}</div>
      </div>
    </motion.div>
  );
}

function SavedRow({ row, isCurrent, draftNeedsConfirm, canSave, busy, now, onOpen, onSaveThenOpen, onDelete }) {
  const [confirm, setConfirm] = useState(null);
  const rowRef = useRef(null);
  const delivered = row.status === 'delivered';
  const items = row.items || [];

  useEffect(() => {
    if (!confirm) return undefined;
    const frame = requestAnimationFrame(() => rowRef.current?.querySelector('[data-autofocus]')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [confirm]);

  const handleOpen = () => {
    if (isCurrent && !draftNeedsConfirm) onOpen(row.id);
    else if (draftNeedsConfirm) setConfirm('replace');
    else onOpen(row.id);
  };

  const cancel = (event) => {
    event?.stopPropagation();
    setConfirm(null);
  };

  return (
    <motion.li
      layout
      {...microInteractions.slideUp}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && confirm) {
          event.preventDefault();
          event.stopPropagation();
          setConfirm(null);
        }
      }}
      className="mb-3 last:mb-0"
    >
      <div
        ref={rowRef}
        data-testid="saved-playlist-row"
        data-playlist-id={row.id}
        data-current={isCurrent || undefined}
        aria-busy={busy || undefined}
        className={`squircle p-4 border transition-[opacity,box-shadow] duration-200 ${busy ? 'opacity-50' : ''} ${isCurrent
          ? 'bg-primary-50/70 dark:bg-primary-900/10 border-primary-200 dark:border-primary-700/40 ring-1 ring-primary-500/20'
          : 'bg-white/80 dark:bg-elevated-dark-2 border-gray-200/80 dark:border-white/5 hover:shadow-soft'}`}
      >
        <div className="flex items-start gap-3">
          <Cover items={items} />
          <div className="min-w-0 flex-1">
            <h3 className="text-body font-semibold text-gray-900 dark:text-gray-100 truncate" title={row.name} data-testid="saved-playlist-name">
              {row.name}
            </h3>
            <p className="mt-0.5 text-caption text-gray-600 dark:text-gray-400" data-testid="saved-playlist-meta">
              {savedRowMeta({ serviceDay: serviceDay(row.service_date), itemCount: items.length, lastDeliveredAt: row.last_delivered_at }, now)}
            </p>
            {isCurrent && (
              <p className={`mt-1 flex items-center gap-1.5 text-caption font-medium ${TONE_CLASSES.muted}`} data-testid="saved-playlist-current">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" aria-hidden="true" />
                {SAVED_COPY.current}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <StatusPill delivered={delivered} />
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setConfirm((c) => (c === 'delete' ? null : 'delete'))}
            disabled={busy}
            aria-label={deleteLabel(row.name)}
            aria-expanded={confirm === 'delete'}
            data-testid="saved-playlist-delete"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 squircle-sm text-body-sm font-medium transition-colors touch-target focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 ${confirm === 'delete'
              ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
              : 'text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {SAVED_COPY.delete}
          </button>
          <Button
            onClick={handleOpen}
            disabled={busy}
            variant="secondary"
            size="sm"
            aria-label={openLabel(row.name)}
            data-testid="saved-playlist-open"
          >
            <FolderOpen className="w-4 h-4 mr-1.5" aria-hidden="true" />
            {SAVED_COPY.open}
          </Button>
        </div>

        <AnimatePresence initial={false}>
          {confirm === 'delete' && (
            <InlineConfirm key="delete" tone="danger" message={deleteConfirm(row.name)} testId="saved-playlist-delete-confirm">
              <Button onClick={cancel} variant="secondary" size="sm" className="flex-1">{COPY.cancel}</Button>
              <Button
                data-autofocus
                onClick={() => {
                  setConfirm(null);
                  onDelete(row);
                }}
                variant="danger"
                size="sm"
                className="flex-1"
                data-testid="saved-playlist-delete-confirm-button"
              >
                {SAVED_COPY.delete}
              </Button>
            </InlineConfirm>
          )}
          {confirm === 'replace' && (
            <InlineConfirm key="replace" tone="info" message={SAVED_COPY.replaceTitle} testId="saved-playlist-replace-confirm">
              {canSave && (
                <Button
                  onClick={() => {
                    setConfirm(null);
                    onSaveThenOpen(row.id);
                  }}
                  variant="secondary"
                  size="sm"
                  className="flex-1 whitespace-nowrap"
                  data-testid="saved-playlist-save-first"
                >
                  {SAVED_COPY.saveFirst}
                </Button>
              )}
              <Button
                data-autofocus
                onClick={() => {
                  setConfirm(null);
                  onOpen(row.id);
                }}
                variant="primary"
                size="sm"
                className="flex-1"
                data-testid="saved-playlist-replace-button"
              >
                {SAVED_COPY.replace}
              </Button>
              <Button onClick={cancel} variant="ghost" size="sm" className="flex-1">{COPY.cancel}</Button>
            </InlineConfirm>
          )}
        </AnimatePresence>
      </div>
    </motion.li>
  );
}

function RowSkeleton() {
  return (
    <div className="squircle p-4 border border-gray-200/80 dark:border-white/5 bg-white/60 dark:bg-elevated-dark-2" aria-hidden="true">
      <div className="flex items-start gap-3">
        <div className="w-16 h-9 rounded-md shimmer bg-gray-200 dark:bg-elevated-dark-3" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/5 rounded shimmer bg-gray-200 dark:bg-elevated-dark-3" />
          <div className="h-3 w-4/5 rounded shimmer bg-gray-200 dark:bg-elevated-dark-3" />
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <div className="h-8 w-20 rounded-lg shimmer bg-gray-200 dark:bg-elevated-dark-3" />
        <div className="h-8 w-20 rounded-lg shimmer bg-gray-200 dark:bg-elevated-dark-3" />
      </div>
    </div>
  );
}

function StateBlock({ Icon, tone = 'muted', children, action, testId }) {
  return (
    <motion.div {...microInteractions.fadeIn} data-testid={testId} className="flex flex-col items-center text-center px-6 py-12">
      <div className={`p-4 squircle mb-4 ${tone === 'error'
        ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
        : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'}`}
      >
        <Icon className="w-7 h-7" aria-hidden="true" />
      </div>
      <p className="text-body-sm text-gray-700 dark:text-gray-300 max-w-[18rem]">{children}</p>
      {action && <div className="mt-5">{action}</div>}
    </motion.div>
  );
}

/**
 * S8 — Saved playlists. A right-hand drawer at ≥768 px and a full-height
 * sheet below. Delete and Open-over-unsaved confirm inline in the row.
 */
export default function SavedPlaylistsDrawer({
  isOpen,
  layout,
  playlists,
  status,
  currentId,
  draftNeedsConfirm,
  canSave,
  deletingIds,
  onOpen,
  onSaveThen,
  onDelete,
  onNew,
  onRetry,
  onClose,
}) {
  const [newConfirm, setNewConfirm] = useState(false);
  const newRef = useRef(null);
  useEffect(() => {
    if (!isOpen) setNewConfirm(false);
  }, [isOpen]);
  useEffect(() => {
    if (!newConfirm) return undefined;
    const frame = requestAnimationFrame(() => newRef.current?.querySelector('[data-autofocus]')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [newConfirm]);
  const handleNew = () => {
    if (draftNeedsConfirm) setNewConfirm((v) => !v);
    else onNew();
  };

  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const isSheet = layout === 'sheet';
  useFocusTrap(panelRef, isOpen, { initialFocusRef: titleRef });

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isOpen) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const showRows = status === 'ready' && playlists.length > 0;
  const motionProps = isSheet
    ? pageTransitions.slideFromBottom
    : { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' }, transition: springs.gentle };

  let body;
  if (status === 'loading' || status === 'idle') {
    body = (
      <div className="space-y-3" data-testid="saved-playlists-loading">
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
      </div>
    );
  } else if (status === 'not_configured') {
    body = (
      <StateBlock Icon={Info} testId="saved-playlists-not-configured">
        <span className={TONE_CLASSES.muted}>{SAVED_COPY.notConfigured}</span>
      </StateBlock>
    );
  } else if (status === 'error') {
    body = (
      <StateBlock
        Icon={AlertTriangle}
        tone="error"
        testId="saved-playlists-error"
        action={(
          <Button onClick={onRetry} variant="secondary" size="md" data-testid="saved-playlists-retry">
            <RotateCw className="w-4 h-4 mr-2" aria-hidden="true" />
            {SAVED_COPY.retry}
          </Button>
        )}
      >
        {SAVED_COPY.error}
      </StateBlock>
    );
  } else if (!showRows) {
    body = (
      <StateBlock Icon={Bookmark} testId="saved-playlists-empty">
        {SAVED_COPY.empty}
      </StateBlock>
    );
  } else {
    body = (
      <ul role="list" data-testid="saved-playlists-list">
        <AnimatePresence initial={false}>
          {playlists.map((row) => (
            <SavedRow
              key={row.id}
              row={row}
              isCurrent={row.id === currentId}
              draftNeedsConfirm={draftNeedsConfirm}
              canSave={canSave}
              busy={deletingIds.includes(row.id)}
              now={now}
              onOpen={onOpen}
              onSaveThenOpen={(id) => onSaveThen({ type: 'open', id })}
              onDelete={onDelete}
            />
          ))}
        </AnimatePresence>
      </ul>
    );
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1001]" key="saved-drawer">
          <motion.div
            {...microInteractions.fadeIn}
            className="absolute inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            ref={panelRef}
            {...motionProps}
            role="dialog"
            aria-modal="true"
            aria-labelledby={TITLE_ID}
            data-testid="saved-playlists-drawer"
            className={`absolute flex flex-col glass glass-border shadow-soft-lg ${isSheet
              ? 'inset-0 safe-top'
              : 'inset-y-0 right-0 w-[380px] xl:w-[420px] max-w-full border-l'}`}
          >
            <div className="flex items-center gap-3 px-5 pt-5 pb-4">
              <div className="p-2.5 squircle-sm bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <Library className="w-5 h-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id={TITLE_ID}
                  ref={titleRef}
                  tabIndex={-1}
                  className="text-heading font-semibold text-gray-900 dark:text-gray-100 focus:outline-none"
                >
                  {SAVED_COPY.drawerTitle}
                </h2>
                {showRows && (
                  <p className="text-caption text-gray-500 dark:text-gray-400" data-testid="saved-playlists-count">
                    {savedCount(playlists.length)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={SAVED_COPY.close}
                data-testid="saved-playlists-close"
                className="flex-shrink-0 p-2.5 squircle-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-elevated-dark-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 touch-target"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              ref={newRef}
              className="px-5 pb-4 border-b border-gray-200/70 dark:border-white/10"
              onKeyDown={(event) => {
                if (event.key === 'Escape' && newConfirm) {
                  event.preventDefault();
                  event.stopPropagation();
                  setNewConfirm(false);
                }
              }}
            >
              <Button
                onClick={handleNew}
                variant="secondary"
                size="md"
                className="w-full"
                aria-expanded={draftNeedsConfirm ? newConfirm : undefined}
                data-testid="saved-playlist-new"
              >
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                {SAVED_COPY.newPlaylist}
              </Button>
              <AnimatePresence initial={false}>
                {newConfirm && (
                  <InlineConfirm key="new" tone="info" message={SAVED_COPY.newTitle} testId="saved-playlist-new-confirm">
                    {canSave && (
                      <Button
                        onClick={() => {
                          setNewConfirm(false);
                          onSaveThen({ type: 'new' });
                        }}
                        variant="secondary"
                        size="sm"
                        className="flex-1 whitespace-nowrap"
                      >
                        {SAVED_COPY.saveFirst}
                      </Button>
                    )}
                    <Button
                      data-autofocus
                      onClick={() => {
                        setNewConfirm(false);
                        onNew();
                      }}
                      variant="primary"
                      size="sm"
                      className="flex-1 whitespace-nowrap"
                      data-testid="saved-playlist-new-confirm-button"
                    >
                      {SAVED_COPY.startNew}
                    </Button>
                    <Button onClick={() => setNewConfirm(false)} variant="ghost" size="sm" className="flex-1">{COPY.cancel}</Button>
                  </InlineConfirm>
                )}
              </AnimatePresence>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 pb-24 md:pb-6">
              {body}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
